import { Link, useLocation } from '@/lib/router-compat';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowRight, Check } from 'lucide-react';
import { FOUNDING_OFFER, findFoundingCity, useFoundingCities } from '@/lib/founding500';
import { useAnalytics } from '@/hooks/useAnalytics';

/**
 * Shared First 500 invitation — the same truthful offer everywhere it appears.
 *
 * City-aware: when the page's market has a landing-enabled founding page the
 * invitation says "Join {City}'s First 500" and links there; otherwise it
 * links to the general /first-500 page. Never shows a counter (founder-gated)
 * and never implies a reservation.
 */
export function FoundingInvite({
  city,
  state,
  variant = 'band',
  className = '',
}: {
  city?: string | null;
  state?: string | null;
  variant?: 'band' | 'compact';
  className?: string;
}) {
  const { data: pages } = useFoundingCities();
  const { trackFunnel } = useAnalytics();
  const location = useLocation();

  const match = findFoundingCity(pages, city, state);
  const headline = FOUNDING_OFFER.headline(match?.city);
  const to = match ? `/founding-500/${match.slug}` : '/first-500';

  const onClick = () =>
    trackFunnel('founding_cta_click', {
      city: match?.city ?? city ?? null,
      destination: to,
      page: location.pathname,
      variant,
    });

  if (variant === 'compact') {
    return (
      <div className={`rounded-2xl border border-primary/30 bg-primary/5 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${className}`}>
        <div className="flex-1 min-w-0">
          <p className="font-display text-lg flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary shrink-0" aria-hidden />
            {headline}
          </p>
          <p className="text-sm text-muted-foreground mt-1">{FOUNDING_OFFER.subline}</p>
        </div>
        <Link to={to} onClick={onClick} className="shrink-0">
          <Button variant="luxury" className="rounded-full gap-2 h-11">
            {headline} <ArrowRight className="h-4 w-4" aria-hidden />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <section className={`py-16 md:py-24 ${className}`} aria-labelledby="founding-invite-title">
      <div className="max-w-4xl mx-auto px-4">
        <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-card px-6 py-12 md:px-12 md:py-16 text-center">
          <div className="absolute inset-0 pointer-events-none" aria-hidden>
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[28rem] h-[28rem] bg-primary/10 rounded-full blur-3xl" />
          </div>
          <div className="relative">
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4 flex items-center justify-center gap-2">
              <Sparkles className="h-4 w-4" aria-hidden /> The First 500
            </p>
            <h2 id="founding-invite-title" className="font-display text-3xl md:text-5xl leading-tight mb-4">
              {headline}
            </h2>
            <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto mb-8">
              {FOUNDING_OFFER.subline}
            </p>
            <ul className="max-w-md mx-auto text-left space-y-2.5 mb-10">
              {FOUNDING_OFFER.points.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm md:text-base">
                  <Check className="h-4 w-4 text-primary mt-1 shrink-0" aria-hidden />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            <Link to={to} onClick={onClick}>
              <Button variant="luxury" size="lg" className="rounded-full gap-2 h-12 px-8">
                Claim Your Free Founding Place <ArrowRight className="h-4 w-4" aria-hidden />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground mt-5 max-w-md mx-auto">{FOUNDING_OFFER.overflowNote}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FoundingInvite;
